import { vec3 } from 'gl-matrix';
import { Ray } from '../math/Ray';
import { Gltf, readBoneCollisionExtras } from '../gltf';
import { RenderObject } from '../object/RenderObject';
import { BoneCollisionBody, BoneCollisionShape, BoneHitResult } from './BoneCollisionBody';
import { Projectile } from './Projectile';

export class BoneHitDetection {
  public readonly targets = new Set<BoneCollisionBody>();
  public debug = false;

  register(body: BoneCollisionBody): void {
    this.targets.add(body);
  }

  unregister(body: BoneCollisionBody): void {
    this.targets.delete(body);
  }

  /**
   * Build a {@link BoneCollisionBody} from an animated render object — applying
   * any per-bone `_BONE_COLLISION` radius/shape overrides on the provided GLB
   * — and register it. No-op (returns null) for non-animated objects.
   */
  registerAnimated(
    obj: RenderObject,
    gltfJson?: Gltf,
    radiusScale: number = 0.4,
  ): BoneCollisionBody | null {
    if (!obj.isAnimated) return null;
    const overrides = gltfJson ? readBoneCollisionOverrideMap(gltfJson) : undefined;
    const body = BoneCollisionBody.fromAnimation(obj, radiusScale, overrides);
    this.register(body);
    return body;
  }

  /**
   * Test all alive projectiles against registered targets.
   * Returns the closest bone hit per projectile (if any).
   *
   * Detection is two-phase:
   *  1. Coarse: ray-segment vs target bounding sphere
   *  2. Fine: ray-segment vs per-bone capsules
   */
  testProjectiles(projectiles: Iterable<Projectile>): Map<Projectile, BoneHitResult> {
    const hits = new Map<Projectile, BoneHitResult>();

    for (const proj of projectiles) {
      if (!proj.alive || proj.settled || proj.boneHitCooldown > 0) continue;

      const prev = proj.body.previousPosition;
      const curr = proj.body.position;
      const segmentLength = vec3.distance(prev as vec3, curr as vec3);
      if (segmentLength < 0.0001) continue;

      const ray = new Ray(prev, curr);

      let closestHit: BoneHitResult | undefined;
      let closestDist = segmentLength;

      for (const target of this.targets) {
        if (target.renderObject === proj.renderObject) continue;

        // Coarse phase: bounding sphere of the entire model
        const sphere = target.renderObject.bounds.boundingSphere;
        const coarseCenter = sphere.position as vec3;
        const coarseRadius = sphere.radius;

        if (!this.raySegmentHitsSphere(ray, segmentLength, coarseCenter, coarseRadius)) {
          continue;
        }

        // Fine phase: per-bone capsules
        if (!target.renderObject.isAnimated || !target.renderObject.animationState) continue;

        const frameSet = target.renderObject.animationState.getFrames();
        const modelMatrix = target.renderObject.modelMatrix;
        const scale = target.renderObject.trs.scale;

        for (const capsule of target.boneCapsules) {
          const { a, b } = target.getWorldCapsuleEndpoints(
            capsule,
            frameSet,
            modelMatrix,
            target.renderObject.boneOverrides,
          );

          const capsuleRadius = capsule.radius * scale;
          let boneT: number | null;

          if (capsule.shape === 'sphere') {
            // Sphere: use child joint position (b) as center
            boneT = this.raySegmentSphereDistance(ray, segmentLength, b, capsuleRadius);
          } else {
            boneT = this.raySegmentCapsuleDistance(ray, segmentLength, a, b, capsuleRadius);
          }

          if (boneT !== null && boneT < closestDist) {
            closestDist = boneT;
            closestHit = {
              target,
              boneCapsule: capsule,
              hitPoint: vec3.scaleAndAdd(vec3.create(), ray.origin, ray.direction, boneT),
              distance: boneT,
            };
          }
        }
      }

      if (closestHit) {
        if (this.debug) {
          console.log(
            `[BoneHitDetection] HIT bone="${closestHit.boneCapsule.jointName}" ` +
              `dist=${closestDist.toFixed(2)} ` +
              `hitPoint=(${closestHit.hitPoint[0].toFixed(1)}, ${closestHit.hitPoint[1].toFixed(1)}, ${closestHit.hitPoint[2].toFixed(1)})`,
          );
        }
        hits.set(proj, closestHit);
      }
    }

    return hits;
  }

  /**
   * Test a single ray against all registered targets.
   * Returns the closest bone hit, or null. Useful for debug click-to-impact.
   */
  testRay(ray: Ray, maxDistance: number = Infinity): BoneHitResult | null {
    let closestHit: BoneHitResult | null = null;
    let closestDist = maxDistance;

    for (const target of this.targets) {
      // Coarse phase: bounding sphere
      const sphere = target.renderObject.bounds.boundingSphere;
      const coarseT = ray.getSphereDistance(sphere.position as vec3, sphere.radius);
      if (coarseT === null || coarseT > closestDist) continue;

      if (!target.renderObject.isAnimated || !target.renderObject.animationState) continue;

      const frameSet = target.renderObject.animationState.getFrames();
      const modelMatrix = target.renderObject.modelMatrix;
      const scale = target.renderObject.trs.scale;

      for (const capsule of target.boneCapsules) {
        const { a, b } = target.getWorldCapsuleEndpoints(
          capsule,
          frameSet,
          modelMatrix,
          target.renderObject.boneOverrides,
        );

        const capsuleRadius = capsule.radius * scale;
        let boneT: number | null;

        if (capsule.shape === 'sphere') {
          boneT = ray.getSphereDistance(b, capsuleRadius);
        } else {
          boneT = ray.getCapsuleDistance(a, b, capsuleRadius);
        }

        if (boneT !== null && boneT < closestDist) {
          closestDist = boneT;
          closestHit = {
            target,
            boneCapsule: capsule,
            hitPoint: vec3.scaleAndAdd(vec3.create(), ray.origin, ray.direction, boneT),
            distance: boneT,
          };
        }
      }
    }

    return closestHit;
  }

  private raySegmentHitsSphere(
    ray: Ray,
    segmentLength: number,
    center: vec3 | number[],
    radius: number,
  ): boolean {
    const distSq = vec3.sqrDist(ray.origin, center);
    if (distSq <= radius * radius) return true;
    const t = ray.getSphereDistance(center, radius);
    return t !== null && t <= segmentLength;
  }

  private raySegmentSphereDistance(
    ray: Ray,
    segmentLength: number,
    center: vec3 | number[],
    radius: number,
  ): number | null {
    const distSq = vec3.sqrDist(ray.origin, center);
    if (distSq <= radius * radius) return 0;
    const t = ray.getSphereDistance(center, radius);
    if (t !== null && t <= segmentLength) return t;
    return null;
  }

  /**
   * Ray-segment vs capsule intersection with origin-inside handling.
   */
  private raySegmentCapsuleDistance(
    ray: Ray,
    segmentLength: number,
    a: vec3,
    b: vec3,
    radius: number,
  ): number | null {
    // Check if origin is inside the capsule volume
    if (this.isPointInsideCapsule(ray.origin, a, b, radius)) return 0;
    const t = ray.getCapsuleDistance(a, b, radius);
    if (t !== null && t <= segmentLength) return t;
    return null;
  }

  /** Check if a point is within radius of a line segment (inside capsule). */
  private isPointInsideCapsule(point: vec3, a: vec3, b: vec3, radius: number): boolean {
    const ab = vec3.sub(vec3.create(), b, a);
    const ap = vec3.sub(vec3.create(), point, a);
    const abLenSq = vec3.dot(ab, ab);
    if (abLenSq < 0.0001) {
      return vec3.sqrDist(point, a) <= radius * radius;
    }
    let t = vec3.dot(ap, ab) / abLenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const closest = vec3.scaleAndAdd(vec3.create(), a, ab, t);
    return vec3.sqrDist(point, closest) <= radius * radius;
  }
}

function readBoneCollisionOverrideMap(
  json: Gltf,
): Map<string, { radius?: number; shape?: BoneCollisionShape }> | undefined {
  const config = readBoneCollisionExtras(json);
  if (!config) return undefined;
  const map = new Map<string, { radius?: number; shape?: BoneCollisionShape }>();
  for (const b of config.bones) {
    map.set(b.joint, { radius: b.radius, shape: b.shape as BoneCollisionShape | undefined });
  }
  return map;
}
